import { useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { backend } from '@/api/backend';
import { ApiError, newIdempotencyKey } from '@/api/http';
import { explainLine } from '@/api/explain';
import { useCompanies } from '@/api/hooks';
import { registerBody, scopeLabel, whyNotRegistrable } from '@/pages/registration';
import { cn } from '@/lib/cn';

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

/** Results shown at once. Beyond this the answer is "keep typing", not a scroll. */
const SHOWN = 12;
/** Below this a query matches most of the catalogue, so it is not yet a query. */
const MIN_QUERY = 2;

interface Hit {
  key: string;
  name: string;
  detail: string;
  registered: boolean;
  bucketId?: string;
  region?: string | null;
  accountId?: string | null;
}

/**
 * Search for a bucket by name, and go to work in it.
 *
 * This began as two flat lists in the rail, which reads fine at the three
 * buckets this account has registered and falls apart at the 197 it observes.
 * Its first replacement was still a list - everything rendered under an input,
 * scrolled. That is the same mistake with a filter on top: a list is a thing
 * you read, and what an operator wants here is to name the bucket they already
 * have in mind and land in it.
 *
 * So this is a combobox, on the WAI-ARIA pattern rather than a div that looks
 * like one: the input owns the listbox, the active option is announced through
 * `aria-activedescendant`, and the arrow keys, Enter and Escape do what they do
 * in every other combobox a person has used. Nothing is listed until a query
 * narrows it, results are bounded, and the count of what did not fit is stated
 * rather than hidden behind a scrollbar.
 *
 * A bucket the account observes but has never registered is offered too, and
 * choosing one registers it and lands on its first stage - a bucket that is not
 * there is imported from the beginning rather than missing from the search.
 *
 * The ceiling is the API, not this file: `/account-buckets` takes a companyId
 * and nothing else, so the whole observed catalogue arrives in one response and
 * is matched here. That holds at hundreds and will not hold at millions, which
 * needs a `q` on the route; see docs/BACKEND_ASKS.md.
 */
export function BucketSearch({ focusOnMount = false }: {
  /** The chooser page is one question, so its box takes focus on arrival. */
  focusOnMount?: boolean;
}) {
  const navigate = useNavigate();
  const { bucketId } = useParams();
  const qc = useQueryClient();
  const companies = useCompanies(true);
  const [query, setQuery] = useState('');
  const deferred = useDeferredValue(query);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focusOnMount) field.current?.focus();
  }, [focusOnMount]);

  const needle = deferred.trim().toLowerCase();
  const searching = needle.length >= MIN_QUERY;

  /**
   * Registered buckets, matched on the server.
   *
   * This read every registered bucket the account holds - following the cursor
   * to exhaustion - and matched the needle in the browser. That is the whole
   * catalogue in memory to answer a question about twelve rows of it, and it
   * does not survive the scale this is built for.
   *
   * `q` was refused with a 400 until the route grew one, then accepted and
   * dropped for a revision, so this is deliberately keyed on the needle and
   * checked against production: `q=cap` returns capway alone, `q=zzzznomatch`
   * returns nothing, and `_` and `%` come back empty because they are escaped
   * as literals rather than run as wildcards.
   */
  const registered = useQuery({
    queryKey: ['live-buckets', needle],
    queryFn: ({ signal }) => backend.listAllBuckets(signal, needle || undefined),
    retry,
    staleTime: 30_000,
  });

  /**
   * The grant used to READ the account listing. Not a scope.
   *
   * A bucket is the only entity in this product: each one stands alone, and
   * nothing groups one to another. This search used to take a company - from
   * the bucket you were in, or from a fallback - and filter to it, so standing
   * inside one bucket narrowed the search to that bucket's neighbours and hid
   * every other bucket in the account behind a boundary that is not part of
   * the architecture.
   *
   * The company survives here for one mechanical reason: `/account-buckets`
   * takes a companyId, so a grant is needed to read the AWS account's listing
   * at all. The listing it returns is the ACCOUNT's - the same buckets under
   * any grant - and only the `registered` flag is relative. So any grant is a
   * key to the same door, and it is used as a key rather than as a filter.
   * The proper fix is a listing that does not ask; see docs/BACKEND_ASKS.md.
   */
  const readWith = companies.data?.items[0]?.id;

  const observed = useQuery({
    // Matched on the server now, so a keystroke reads twelve rows rather than
    // the account. The needle is part of the key, so each query caches.
    queryKey: ['account-buckets', readWith, needle],
    queryFn: ({ signal }) => backend.listAccountBuckets(readWith!, needle, SHOWN, signal),
    enabled: Boolean(readWith) && searching,
    retry,
    staleTime: 60_000,
  });

  const { hits, more } = useMemo(() => {
    if (!searching) return { hits: [] as Hit[], more: 0 };
    // Already matched by the server; narrowing again here would only be a
    // second, weaker implementation of the same rule.
    const mine: Hit[] = (registered.data?.items ?? []).map((b) => ({
        key: `r:${b.bucket.id}`,
        name: scopeLabel(b.bucket.name, (b as { prefix?: string }).prefix ?? ''),
        detail: 'registered',
        registered: true,
        bucketId: b.bucket.id,
      }));
    const known = new Set(mine.map((h) => h.name));
    const notYet: Hit[] = (observed.data?.items ?? [])
      .filter((b) => !b.registered && !known.has(b.bucketName) && b.bucketName.toLowerCase().includes(needle))
      .map((b) => ({
        key: `o:${b.bucketName}`,
        name: b.bucketName,
        detail: 'register',
        registered: false,
        region: b.bucketRegion ?? null,
        accountId: (b as { accountId?: string }).accountId ?? null,
      }));
    const all = [...mine, ...notYet];
    /**
     * How many more MATCHED, not how many more arrived.
     *
     * This subtracted from what one response happened to hold, and the
     * observed listing is asked for twelve rows, so "N more match" described
     * the page rather than the account. The route carries `matchedCount` for
     * exactly this reason.
     *
     * Counted over the observed half alone, deliberately. The registered half
     * is read to exhaustion with the same needle, so all of it is already on
     * screen and none of it is hidden behind this number; adding the two
     * totals would double-count every bucket that is both observed and
     * registered, and an overstated remainder is its own false claim.
     */
    const shown = all.slice(0, SHOWN);
    const shownUnregistered = shown.filter((h) => !h.registered).length;
    const matchedObserved = observed.data?.matchedCount ?? notYet.length;
    return { hits: shown, more: Math.max(0, matchedObserved - shownUnregistered) };
  }, [searching, registered.data, observed.data, needle]);

  useEffect(() => setActive(0), [needle]);

  // A click outside closes it, which is the one behaviour a keyboard-complete
  // combobox still needs from the mouse.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const importIt = async (hit: Hit) => {
    // No companyId: the contract made it optional and the server mints an
    // owner for this bucket alone. Filing it under whichever grant happened to
    // read the listing is what put three unrelated corpora under one company.
    //
    // There used to be a `if (!readWith) return` above this, a press that did
    // nothing, said nothing and left the row looking untouched. A control
    // either acts or says why it did not.
    const scope = { name: hit.name, region: hit.region ?? null, accountId: hit.accountId ?? null };
    const refusal = whyNotRegistrable(scope);
    if (refusal) {
      setNote(refusal);
      return;
    }
    setBusy(hit.key);
    setNote(null);
    try {
      const r = await backend.registerBucket(registerBody(scope), newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: ['live-buckets'] });
      void qc.invalidateQueries({ queryKey: ['account-buckets'] });
      close();
      navigate(`/buckets/${r.bucket.id}`);
    } catch (e) {
      setNote(explainLine(e, `register ${hit.name}`));
    } finally {
      setBusy(null);
    }
  };

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const choose = (hit: Hit) => {
    if (hit.registered && hit.bucketId) {
      close();
      navigate(`/buckets/${hit.bucketId}`);
      return;
    }
    void importIt(hit);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return close();
    if (!hits.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % hits.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + hits.length) % hits.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[active];
      if (hit) choose(hit);
    }
  };

  const expanded = open && searching;

  return (
    <div ref={box} className="border-b border-line px-2.5 pb-2 pt-2">
      {/* A company picker sat here, above the search, and switching it left
          the bucket you were in for a page listing that company's buckets.
          There is no such level: a bucket is the whole entity, and the only
          thing to choose between is one bucket and another. */}

      {/*
        * The results float over the rail instead of sitting in it.
        *
        * The listbox was a sibling in the flow, so opening it pushed the stages
        * down the rail and closing it pulled them back - the navigation moving
        * because someone typed. A dropdown is an overlay: it covers what is
        * behind it and puts everything back untouched.
        */}
      <div className="relative">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-ink-4">
          <Search className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        <input
          ref={field}
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={expanded && hits[active] ? `${listId}-${active}` : undefined}
          aria-label="Find a bucket"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setOpen(true);
            setQuery(e.target.value);
          }}
          onKeyDown={onKey}
          placeholder="Search every bucket…"
          className="w-full rounded border border-line bg-panel-2 py-1.5 pl-7 pr-2 text-small text-ink outline-none placeholder:text-ink-4 focus:border-acc-2"
        />

        {open && !searching && query.trim().length > 0 && (
          <p className="absolute left-0 right-0 top-full z-30 mt-1.5 rounded border border-line bg-panel px-2 py-1.5 text-caption text-ink-4 shadow-pop">
            Keep typing: at least {MIN_QUERY} characters.
          </p>
        )}

        {expanded && (
          <ul
            id={listId}
            role="listbox"
            className="absolute left-0 right-0 top-full z-30 mt-1.5 max-h-80 overflow-y-auto rounded border border-line bg-panel shadow-pop"
          >
          {observed.isPending && hits.length === 0 && (
            <li className="px-2 py-2 text-caption text-ink-3">Searching the account’s buckets.</li>
          )}
          {hits.map((hit, i) => (
            /*
             * No key handler on the option, deliberately.
             *
             * In the `aria-activedescendant` combobox the options are not
             * focusable and never receive keys: the input keeps focus and owns
             * ArrowUp, ArrowDown, Enter and Escape, which is what makes the
             * whole control operable from the keyboard. Adding a listener here
             * to satisfy the rule would attach it to an element that cannot be
             * focused, which is the appearance of accessibility rather than the
             * thing itself.
             */
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={hit.key}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(hit)}
              className={cn(
                'flex cursor-pointer items-baseline gap-2 px-2 py-1',
                i === active && 'bg-hov',
                hit.bucketId === bucketId && 'bg-acc-soft',
              )}
            >
              <span className={cn('mono min-w-0 flex-1 truncate text-small', hit.registered ? 'text-ink' : 'text-ink-2')}>
                {hit.name}
              </span>
              <span className={cn('flex-none text-caption', hit.registered ? 'text-ink-4' : 'text-acc-2')}>
                {busy === hit.key ? 'registering…' : hit.detail}
              </span>
            </li>
          ))}
            {/* A failed read is not an empty answer.
                Both of these could fail and this said "No bucket matches",
                which tells an operator their bucket does not exist when what
                actually happened is that the catalogue could not be read. It
                is the most consequential empty state in the product: the reply
                to the only question the landing page asks. */}
            {hits.length === 0 && (registered.isError || observed.isError) && (
              <li className="px-2 py-2">
                <span role="alert" className="flex flex-wrap items-center gap-2 text-caption text-warn">
                  <span className="min-w-0 flex-1">
                    {explainLine(registered.error ?? observed.error, 'read the buckets')} Nothing here is a statement
                    about whether “{query.trim()}” exists.
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (registered.isError) void registered.refetch();
                      if (observed.isError) void observed.refetch();
                    }}
                    className="flex-none rounded border border-line bg-panel px-2 py-0.5 text-acc-2 transition-colors duration-fast hover:bg-hov"
                  >
                    Try again
                  </button>
                </span>
              </li>
            )}
            {hits.length === 0 && !observed.isPending && !registered.isError && !observed.isError && (
              <li className="px-2 py-2 text-caption text-ink-3">No bucket matches “{query.trim()}”.</li>
            )}
            {more > 0 && (
              <li className="border-t border-line px-2 py-1 text-caption text-ink-4">
                {more.toLocaleString()} more match. Keep typing to narrow them.
              </li>
            )}
          </ul>
        )}
      </div>

      {note && (
        <p role="alert" className="mt-1.5 text-caption text-warn">
          {note}
        </p>
      )}
    </div>
  );
}
